import React from 'react';
import styled from 'styled-components';
import { convertSizeToString, SizeData } from '../../shared/dto/EntityRepository';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { StyledDivComponent } from '../../shared/dto/StyleCompRepository';
import { InputWork } from '../../shared/component/headless/input/InputBody';
import HeadlessInput from '../../shared/component/headless/input/HeadlessInput';
import { useValidation } from '../../shared/hooks/useValidation';
import { INPUT_SIZE_MIN_LENGTH, INPUT_SIZE_MAX_LENGTH } from '../../shared/constants/limits';
import { getSizeUnitDisplayValue, SizeUnit } from '../../shared/enum/EnumRepository';


// 2-D  ➜ width × height
// 3-D  ➜ width × height × depth
interface SizeInputForPEProps {
  value: SizeData;
  onChange: (next: SizeData) => void;
  StyledDiv: StyledDivComponent;
}


const SizeInputForPE: React.FC<SizeInputForPEProps> = ({
  value,
  onChange,
  StyledDiv,
}) => {
  const { isEditMode } = useEditMode();
  const { checkType } = useValidation();

  const handleChange = (field: keyof SizeData, newValue: string) => {
    const validationField = field === 'dpi' ? 'width' : field;
    if (!checkType(validationField, newValue)) {
      return;
    };
    const updatedSize = { ...value, [field]: newValue };
    onChange(updatedSize);
  }

  const isPxUnit = value.unit === SizeUnit.PX;

  const renderPxSizeInputs = () => (
    <>
      <HeadlessInput
        value={value.height || ''}
        handleChange={(e) => handleChange('height', e.target.value)}
        StyledInput={NumInput}
        placeholder={"H"}
        minLimit={INPUT_SIZE_MIN_LENGTH}
        maxLimit={INPUT_SIZE_MAX_LENGTH}
      />
      <Times>×</Times>
      <HeadlessInput
        value={value.width || ''}
        handleChange={(e) => handleChange('width', e.target.value)}
        StyledInput={NumInput}
        placeholder={"W"}
        minLimit={INPUT_SIZE_MIN_LENGTH}
        maxLimit={INPUT_SIZE_MAX_LENGTH}
      />
      <Unit>px</Unit>
      <Separator>,</Separator>
      <HeadlessInput
        value={value.dpi || ''}
        handleChange={(e) => handleChange('dpi', e.target.value)}
        StyledInput={NumInput}
        placeholder={"D"}
        minLimit={INPUT_SIZE_MIN_LENGTH}
        maxLimit={INPUT_SIZE_MAX_LENGTH}
      />
      <Unit>dpi</Unit>
    </>
  );

  const renderDefaultSizeInputs = () => (
    <>
      <HeadlessInput
        value={value.height || ''}
        handleChange={(e) => handleChange('height', e.target.value)}
        StyledInput={NumInput}
        placeholder={"H"}
        minLimit={INPUT_SIZE_MIN_LENGTH}
        maxLimit={INPUT_SIZE_MAX_LENGTH}
      />
      <Times>×</Times>
      <HeadlessInput
        value={value.width || ''}
        handleChange={(e) => handleChange('width', e.target.value)}
        StyledInput={NumInput}
        placeholder={"W"}
        minLimit={INPUT_SIZE_MIN_LENGTH}
        maxLimit={INPUT_SIZE_MAX_LENGTH}
      />
      <Times>×</Times>
      <HeadlessInput
        value={value.depth || ''}
        handleChange={(e) => handleChange('depth', e.target.value)}
        StyledInput={NumInput}
        placeholder={"D"}
        minLimit={INPUT_SIZE_MIN_LENGTH}
        maxLimit={INPUT_SIZE_MAX_LENGTH}
      />
      <Unit>{getSizeUnitDisplayValue(value.unit)}</Unit>
    </>
  );

  return (
    <>
      {isEditMode ? (
        <SizeInputContainer>
          <SizeInputComp>
            {isPxUnit ? renderPxSizeInputs() : renderDefaultSizeInputs()}
          </SizeInputComp >
        </SizeInputContainer>
      ) : (
        <StyledDiv>{convertSizeToString(value)}</StyledDiv>
      )}
    </>
  );
};

const SizeInputContainer = styled.div`
  width: fit-content;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  ${({ theme }) => theme.typography.Body_04};
`;

const SizeInputComp = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const NumInput = styled(InputWork)`
  width: auto;
  min-width: 10px;
  text-align: center;
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
`;

const Times = styled.span`
  user-select: none;
`;

const Separator = styled.span`
  margin-right: 4px;
  user-select: none;
`;

const Unit = styled.span`
  margin-left: 4px;
  min-width: 10px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_04};
`;

export default SizeInputForPE;
